import { Injectable, inject } from '@angular/core';
import { of, Subject, throwError } from 'rxjs';
import { HttpParams, HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { debounceTime, switchMap, catchError, first, map } from 'rxjs/operators';
import { Event, PayoutMethod, PayoutStatus, SaleRecord, Ticket, TicketRecall } from '../../models';
import { NotificationService } from '../../services';
import { captureException } from '@sentry/angular';
import { PendingSell } from '../../platform/saleV2/sale.types';

@Injectable({
  providedIn: 'root',
})
export class AdminTicketService {
  private http = inject(HttpClient);
  private notificationService = inject(NotificationService);

  configUrl = environment.apiUrl;

  public getMoreTicketsByEvent = new Subject<{
    limit: number,
    offset: number,
    searchQuery: string,
    categories: string[],
    payoutStatus: string[],
    purchaseFlow: string[],
    status: string[],
    ticketingPlatform: string[],
    dateRangeStart: string,
    dateRangeEnd: string,
  }>();

  public getMorePendingSell = new Subject<{
    limit: number,
    offset: number,
    searchQuery: string,
  }>();

  cardRefundCeil(eventId: number, ticketNumbers: string[]) {
    const route = `${this.configUrl}a/t/cardRefundCeil/${eventId}`;

    const params = new HttpParams({
      fromObject: {
        ...ticketNumbers?.filter( (t) => !!t)?.reduce( (acc, t, i) => ({[`ticketNumbers[${i}]`]: t, ...acc}), {}),
      },
    });

    return this.http.get<{refundCeilPerTicket: {[ticketNumber: string]: number}}>(route, {params}).pipe(
      catchError((err) => {
        captureException(err);
        return of(undefined);
      }),
      first(),
    );
  }

  getTicketsByEvent(eventId: number) {
    const route = `${this.configUrl}events/${eventId}/allTicketsData`;
    return this.getMoreTicketsByEvent.pipe(
      // wait 300ms after each keystroke before considering the term
      debounceTime(600),
      // switch to new search observable each time the term changes
      switchMap( ({
        limit,
        offset,
        searchQuery,
        categories,
        status,
        ticketingPlatform,
        payoutStatus,
        purchaseFlow,
        dateRangeStart,
        dateRangeEnd,
      }) => {
        const params = new HttpParams({
          fromObject: {
            limit: limit.toString(),
            offset: offset.toString(),
            searchQuery: encodeURIComponent(searchQuery?.toString()),
            ...categories?.filter( (c) => !!c)?.reduce( (acc, c, i) => ({[`categories[${i}]`]: c, ...acc}), {}),
            ...payoutStatus?.filter( (ps) => !!ps)?.reduce( (acc, ps, i) => ({[`payoutStatus[${i}]`]: ps, ...acc}), {}),
            ...purchaseFlow?.filter( (pf) => !!pf)?.reduce( (acc, pf, i) => ({[`purchaseFlow[${i}]`]: pf, ...acc}), {}),
            ...status?.filter( (s) => !!s)?.reduce( (acc, s, i) => ({[`status[${i}]`]: s, ...acc}), {}),
            ...ticketingPlatform?.filter( (tp) => !!tp)?.reduce( (acc, tp, i) => ({[`ticketingPlatform[${i}]`]: tp, ...acc}), {}),
            dateRangeStart: dateRangeStart ? dateRangeStart.toString() : '',
            dateRangeEnd: dateRangeEnd ? dateRangeEnd.toString() : '',
          },
        });
        return this.http.get<{rows: Ticket[], count: number}>(route, {params});
      }),
      map((res) => {
        res.rows = res.rows.map((t) =>
          new Ticket({...t, category: t.Category.name}),
        );
        return res;
      }),
    );
  }

  getPendingSells() {
    const route = `${this.configUrl}a/t/allPendingSell`;
    return this.getMorePendingSell.pipe(
      debounceTime(600),
      switchMap(({
        limit,
        offset,
        searchQuery,
      }) => {
        const params = new HttpParams({
          fromObject: {
            limit: limit.toString(),
            offset: offset.toString(),
            searchQuery: encodeURIComponent(searchQuery?.toString()),
          },
        });
        return this.http.get<{rows: (PendingSell & {event: Partial<Event>} )[], count: number}>(route, {params});
      }),
    );
  }

  updatePendingSell(pendingSell: PendingSell) {
    const route = `${this.configUrl}a/t/pendingSell`;
    return this.http.put<PendingSell>(route, {pendingSell}).pipe(
      first(),
    );
  }

  deletePendingSell(pendingSellId: number) {
    const route = `${this.configUrl}a/t/pendingSell/${pendingSellId}`;
    return this.http.delete<{deletedNb: number}>(route).pipe(
      first(),
    );
  }

  getGeneratedTicketByEvent(
    ticketId: number,
    eventId: number,
    useOriginalBarcode = false,
    useNewTicketBarcode?: string,
  ) {
    const route = `${this.configUrl}t/g/${eventId}/${ticketId}?useOriginalBarcode=${useOriginalBarcode}&useNewTicketBarcode=${useNewTicketBarcode}`;
    return this.http.get<{
      fixedLength: string,
      randomLenght: string,
      // eslint-disable-next-line id-denylist
      number: string,
      hash: string,
      pdf: any
    }>(route).pipe(
      first(),
      catchError((error) => {
        this.notificationService.open(error.error.ResultMessage);
        return throwError(error);
      }),
    );
  }

  markAsPaid(saleRecordId: number, payoutIdOrMessage: string, payoutStatus: PayoutStatus) {
    const route = `${this.configUrl}t/u/payoutStatus`;

    return this.http.put(route, {saleRecordId, payoutIdOrMessage, payoutStatus}).pipe(
    );
  }

  editPayoutMethod(eventId: number, saleRecordId: number, payoutMethod: PayoutMethod) {
    const route = `${this.configUrl}t/u/payoutMethod/${eventId}`;

    return this.http.put(route, {saleRecordId, payoutMethod}).pipe(
    );
  }

  getPimsTicketsAccessControl<T>(pimsEventId: string, reelaxEventId: number, page = '1') {
    const route = `${this.configUrl}w/a/getPimsTicketsAccessControlList/${pimsEventId}`;
    const params = new HttpParams({
      fromObject: {
        reelaxEventId: reelaxEventId.toString(),
        page,
      },
    });
    return this.http.get<T>(route, {params});
  }

  getPimsChannelById<T>(pimsChannelId: string) {
    const route = `${this.configUrl}w/a/getPimsChannel/${pimsChannelId}`;
    return this.http.get<T>(route);
  }

  getPimsCategoryById<T>(pimsCategoryId: string) {
    const route = `${this.configUrl}w/a/getPimsCategory/${pimsCategoryId}`;
    return this.http.get<T>(route);
  }

  changeTicketSeller(ticketId: number, newOwnerId: number) {
    const route = `${this.configUrl}t/u/seller/${ticketId}/${newOwnerId}`;

    return this.http.put<{success: boolean; saleRecord?: SaleRecord}>(route, {}).pipe(
    );
  }

  changeTicketBuyer(ticketId: number, newOwnerId: number) {
    const route = `${this.configUrl}t/u/buyer/${ticketId}/${newOwnerId}`;

    return this.http.put<{success: boolean; saleRecord?: SaleRecord}>(route, {}).pipe(
    );
  }

  changeCategory(ticketId: number, oldCategoryId: number, newCategoryId: number) {
    const route = `${this.configUrl}t/u/category/${ticketId}`;

    return this.http.put<{success: boolean; ticket?: Ticket}>(route, {oldCategoryId, newCategoryId}).pipe(
    );
  }

  getStalledTickets(searchQuery: string, limit: number, minDate?: Date, maxDate?: Date) {
    const route = `${this.configUrl}a/t/exploreTicketsStalled`;
    const params = new HttpParams({
      fromObject: {
        searchQuery: encodeURIComponent(searchQuery.toString()),
        maxDate: maxDate?.toISOString(),
        minDate: minDate?.toISOString(),
        limit,
      },
    });
    return this.http.get<{
      eventsData: {[eventId: number]: {
        auto: { [categoryId: number]: string },
        autoMax: { [categoryId: number]: string },
        wait: {
          ticketsNb: { [categoryId: number]: string },
          usersNb: { [categoryId: number]: string },
        },
      }},
      stalledTickets: {rows: Ticket[], count: number}
    }>(route, {params}).pipe(
      first(),
    );
  }

  createTicketsRecall(toRecall: {ticketId: number, eventId: number, sellerId: number}[]) {
    const route = `${this.configUrl}a/t/createTicketsRecall`;
    return this.http.post<Array<[TicketRecall, boolean]>>(route, {toRecall});
  }

  getTicketsRecalls(limit: number, minDate?: Date) {
    const route = `${this.configUrl}a/t/getTicketsRecalls`;
    const params = new HttpParams({
      fromObject: {
        minDate: minDate?.toISOString(),
        limit,
      },
    });
    return this.http.get<{rows: TicketRecall[], count: number}>(route, {params}).pipe(
      first(),
    );
  }

  deleteTicketsRecall(id: number) {
    const route = `${this.configUrl}a/t/deleteTicketRecall/${id}`;
    return this.http.delete<{deletedNb: number}>(route);
  }

}
